'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { useEventCallback } from 'usehooks-ts';
import { codeSessionRequest, jsonBody } from '@/data/code-sessions/runtime/cloud';
import { handOverPageChat } from '@/lib/chat/page-chat-handoff';
import { clientLogger } from '@/lib/client-logger';
const PAGE_CHAT_WARM_ROUTE = '/api/chat/code-session/warm';

/**
 * How often an open page keeps its chat's warm session warming: well within the session's five
 * minutes (`WARM_SESSION_TTL_MS`), so someone who reads the pull request for a while before
 * asking still asks a ready agent.
 */
export const PAGE_CHAT_WARM_REFRESH_MS = 2 * 60 * 1000;

/** Boots, or keeps, the session the chat's first message will start (`POST /api/chat/code-session/warm`). */
export function warmPageChatSession(input: {
  sessionId: string;
  pullRequestId: string;
  model: string | undefined;
}) {
  return codeSessionRequest<{
    warm: boolean;
  }>(PAGE_CHAT_WARM_ROUTE, jsonBody(input));
}

/**
 * Starts the chat's session as soon as the page opens, while the person reads the pull request,
 * so their first message goes to a ready agent instead of waiting for a sandbox to start. It
 * keeps the session warming while the page is in view; a page left in the background lets it
 * expire, and warms it again when it is back in view. A reload goes on with the chat and the
 * session warming for it instead of booting another (`handOverPageChat`). Off once the chat has a
 * session.
 *
 * Returns whether the server warms the chat's session, by its last answer for this chat: the
 * chat then reads that session's sandbox starting (`useCodeSessionChat`'s `preloading`), and keeps
 * reading it as its first message claims the session.
 */
export function useWarmPageChatSession(input) {
  const $ = _c(6);
  const {
    chatId,
    pullRequestId,
    enabled
  } = input;
  const resolveModel = useEventCallback(input.model);
  const [warmedChatId, setWarmedChatId] = useState(null);
  let t0;
  let t1;
  if ($[0] !== chatId || $[1] !== enabled || $[2] !== pullRequestId || $[3] !== resolveModel) {
    t0 = () => {
      if (!enabled || pullRequestId === null) {
        return;
      }
      const listening = new AbortController();
      let active = true;
      let timer;
      let warmedAt;
      const warm = () => {
        if (document.visibilityState !== "visible") {
          return;
        }
        warmedAt = Date.now();
        warmPageChatSession({
          sessionId: chatId,
          pullRequestId,
          model: resolveModel()
        }).then(result => {
          if (!active) {
            return;
          }
          setWarmedChatId(result.warm ? chatId : null);
          if (!result.warm) {
            window.clearInterval(timer);
            listening.abort();
          }
        }).catch(error => {
          clientLogger.warn("[page-chat] Warming the chat session failed", {
            chatId,
            error
          });
        });
      };
      const handOver = event => {
        if (!event.persisted && warmedAt !== undefined) {
          handOverPageChat(pullRequestId, {
            chatId,
            warmedAt
          });
        }
      };
      timer = window.setInterval(warm, PAGE_CHAT_WARM_REFRESH_MS);
      document.addEventListener("visibilitychange", warm, {
        signal: listening.signal
      });
      window.addEventListener("pagehide", handOver, {
        signal: listening.signal
      });
      warm();
      return () => {
        active = false;
        window.clearInterval(timer);
        listening.abort();
      };
    };
    t1 = [chatId, enabled, pullRequestId, resolveModel];
    $[0] = chatId;
    $[1] = enabled;
    $[2] = pullRequestId;
    $[3] = resolveModel;
    $[4] = t0;
    $[5] = t1;
  } else {
    t0 = $[4];
    t1 = $[5];
  }
  useEffect(t0, t1);
  return warmedChatId === chatId;
}